import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2, Play, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { rebateAlertsAPI } from '../../lib/api';

const blankStage = () => ({ threshold_days: 30, kind: 'cash', amount: '', en: '', fr: '', es: '' });
const toStage = (s) => ({
    threshold_days: Number(s.threshold_days),
    incentive: s.kind === 'cash'
        ? { kind: 'cash', amount: Number(s.amount) }
        : { kind: 'perk', label: { en: s.en.trim(), fr: s.fr.trim() || null, es: s.es.trim() || null } },
});
const fromStage = (s) => ({
    threshold_days: s.threshold_days,
    kind: s.incentive?.kind || 'cash',
    amount: s.incentive?.amount ?? '',
    en: s.incentive?.label?.en || '', fr: s.incentive?.label?.fr || '', es: s.incentive?.label?.es || '',
});

const IN = 'w-full rounded-md bg-slate-800 border border-slate-700 px-2.5 py-1.5 text-sm text-slate-100';

/**
 * Aging-incentive SCHEDULES — the missing half of the specials engine. Without
 * a schedule no vehicle can ever become eligible, so the Promotions queue stays
 * empty forever no matter how long a car sits.
 */
export const CCSpecialSchedules = ({ organizationId }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [draft, setDraft] = useState(null);   // { id?, en, fr, es, active, stages[] }

    const load = useCallback(async () => {
        if (!organizationId) return;
        try {
            const r = await rebateAlertsAPI.schedules(organizationId);
            setRows(r.data?.schedules || r.data || []);
        } catch { setRows([]); }
    }, [organizationId]);
    useEffect(() => { load(); }, [load]);

    const startNew = () => setDraft({ en: '', fr: '', es: '', active: true, stages: [blankStage()] });
    const startEdit = (s) => setDraft({
        id: s.id, en: s.name?.en || '', fr: s.name?.fr || '', es: s.name?.es || '',
        active: s.active !== false, stages: (s.stages || []).map(fromStage),
    });

    const save = async () => {
        if (!draft.en.trim()) { toast.error(t('specialSched.nameRequired', 'A schedule name is required')); return; }
        const bad = draft.stages.find((s) => !(Number(s.threshold_days) > 0)
            || (s.kind === 'cash' ? !(Number(s.amount) > 0) : !s.en.trim()));
        if (bad) { toast.error(t('specialSched.stageInvalid', 'Every stage needs days and an incentive')); return; }
        const body = {
            organization_id: organizationId,
            name: { en: draft.en.trim(), fr: draft.fr.trim() || null, es: draft.es.trim() || null },
            active: draft.active,
            stages: draft.stages.map(toStage),
            notify_rules: [{ type: 'on_eligible' }],
        };
        setBusy(true);
        try {
            if (draft.id) await rebateAlertsAPI.updateSchedule(draft.id, body);
            else await rebateAlertsAPI.createSchedule(body);
            toast.success(t('specialSched.saved', 'Schedule saved'));
            setDraft(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong')); }
        finally { setBusy(false); }
    };

    const remove = async (id) => {
        setBusy(true);
        try { await rebateAlertsAPI.deleteSchedule(id); await load(); }
        catch (e) { toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong')); }
        finally { setBusy(false); }
    };

    const runNow = async () => {
        setBusy(true);
        try {
            const r = await rebateAlertsAPI.evaluate(organizationId);
            toast.success(t('specialSched.evaluated', '{{n}} vehicle(s) now eligible', { n: r.data?.created ?? 0 }));
        } catch (e) { toast.error(e.response?.data?.detail || t('common.error', 'Something went wrong')); }
        finally { setBusy(false); }
    };

    const upd = (i, patch) => setDraft((d) => ({
        ...d, stages: d.stages.map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));

    return (
        <div className="space-y-4" data-testid="cc-special-schedules">
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm text-slate-400 flex-1 min-w-[12rem]">
                    {t('specialSched.subtitle', 'After this many days in inventory, a vehicle proposes this incentive for your approval.')}
                </p>
                <Button size="sm" onClick={startNew} disabled={busy} data-testid="sched-new">
                    <Plus className="w-3.5 h-3.5 mr-1" />{t('specialSched.new', 'New schedule')}
                </Button>
                <Button size="sm" variant="outline" onClick={runNow} disabled={busy} data-testid="sched-evaluate">
                    <Play className="w-3.5 h-3.5 mr-1" />{t('specialSched.evaluate', 'Check inventory now')}
                </Button>
            </div>

            {rows.length === 0 && !draft ? (
                <p className="text-sm text-amber-500" data-testid="sched-empty">
                    {t('specialSched.empty', 'No schedule yet — until one exists, no vehicle can become eligible for a promotion.')}
                </p>
            ) : null}

            {rows.map((s) => (
                <div key={s.id} className="rounded-xl border border-slate-800 p-3" data-testid={`sched-row-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-100 truncate">{s.name?.en}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                                {(s.stages || []).map((st) => `${st.threshold_days}d → ${st.incentive?.kind === 'cash' ? `$${st.incentive.amount}` : st.incentive?.label?.en}`).join('  ·  ')}
                            </p>
                        </div>
                        <span className={`text-[10px] uppercase px-2 py-1 rounded-full shrink-0 ${s.active !== false ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-700 text-slate-300'}`}>
                            {s.active !== false ? t('specialSched.active', 'Active') : t('specialSched.paused', 'Paused')}
                        </span>
                    </div>
                    <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" onClick={() => startEdit(s)} data-testid={`sched-edit-${s.id}`}>{t('common.edit', 'Edit')}</Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`sched-delete-${s.id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" />{t('common.delete', 'Delete')}
                        </Button>
                    </div>
                </div>
            ))}

            {draft ? (
                <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-3 space-y-3" data-testid="sched-form">
                    <input value={draft.en} onChange={(e) => setDraft({ ...draft, en: e.target.value })} className={IN}
                        placeholder={t('specialSched.nameEn', 'Schedule name (English)')} data-testid="sched-name-en" />
                    <div className="grid grid-cols-2 gap-2">
                        <input value={draft.fr} onChange={(e) => setDraft({ ...draft, fr: e.target.value })} className={IN}
                            placeholder={t('specialSched.nameFr', 'Name (French)')} data-testid="sched-name-fr" />
                        <input value={draft.es} onChange={(e) => setDraft({ ...draft, es: e.target.value })} className={IN}
                            placeholder={t('specialSched.nameEs', 'Name (Spanish)')} data-testid="sched-name-es" />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-slate-300">
                        <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="sched-active" />
                        {t('specialSched.activeLabel', 'Active')}
                    </label>

                    {draft.stages.map((s, i) => (
                        <div key={i} className="rounded-lg border border-slate-800 p-2.5 space-y-2" data-testid={`sched-stage-${i}`}>
                            <div className="flex items-center gap-2">
                                <input type="number" value={s.threshold_days} onChange={(e) => upd(i, { threshold_days: e.target.value })}
                                    className={`${IN} w-24`} placeholder={t('specialSched.days', 'Days')} data-testid={`sched-stage-days-${i}`} />
                                <span className="text-xs text-slate-400">{t('specialSched.daysInInventory', 'days in inventory')}</span>
                                <select value={s.kind} onChange={(e) => upd(i, { kind: e.target.value })}
                                    className={`${IN} w-28 ml-auto`} data-testid={`sched-stage-kind-${i}`}>
                                    <option value="cash">{t('specialSched.cash', 'Cash off')}</option>
                                    <option value="perk">{t('specialSched.perk', 'Perk')}</option>
                                </select>
                            </div>
                            {s.kind === 'cash' ? (
                                <input type="number" value={s.amount} onChange={(e) => upd(i, { amount: e.target.value })}
                                    className={IN} placeholder={t('specialSched.amount', 'Amount ($)')} data-testid={`sched-stage-amount-${i}`} />
                            ) : (
                                <div className="grid grid-cols-3 gap-2">
                                    <input value={s.en} onChange={(e) => upd(i, { en: e.target.value })} className={IN} placeholder="EN" data-testid={`sched-stage-en-${i}`} />
                                    <input value={s.fr} onChange={(e) => upd(i, { fr: e.target.value })} className={IN} placeholder="FR" data-testid={`sched-stage-fr-${i}`} />
                                    <input value={s.es} onChange={(e) => upd(i, { es: e.target.value })} className={IN} placeholder="ES" data-testid={`sched-stage-es-${i}`} />
                                </div>
                            )}
                            {draft.stages.length > 1 ? (
                                <Button size="sm" variant="ghost" onClick={() => setDraft({ ...draft, stages: draft.stages.filter((_, idx) => idx !== i) })}
                                    data-testid={`sched-stage-remove-${i}`}>{t('specialSched.removeStage', 'Remove stage')}</Button>
                            ) : null}
                        </div>
                    ))}

                    <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => setDraft({ ...draft, stages: [...draft.stages, blankStage()] })} data-testid="sched-add-stage">
                            <Plus className="w-3.5 h-3.5 mr-1" />{t('specialSched.addStage', 'Add stage')}
                        </Button>
                        <Button size="sm" onClick={save} disabled={busy} data-testid="sched-save">
                            <Save className="w-3.5 h-3.5 mr-1" />{t('common.save', 'Save')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDraft(null)} data-testid="sched-cancel">
                            <X className="w-3.5 h-3.5 mr-1" />{t('common.cancel', 'Cancel')}
                        </Button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default CCSpecialSchedules;
